// firebaseStorageService.ts - Handle Firebase Firestore operations

import { firestore } from '@/lib/firebase';
import {
  collection,
  doc,
  addDoc,
  setDoc,
  getDoc,
  getDocs,
  deleteDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';

type FirestoreTimestamp = typeof Timestamp;
import { CheckResult } from './checkDefinitions';
import { AnalysisParameters } from './workingDocumentProcessor';
import { secureLog } from './securityUtils';

export interface UserQuestionAnswer {
  questionId: string;
  answer: 'yes' | 'no' | 'na' | string; // Allow custom string answers for dropdown questions
  note: string;
}

/**
 * Recursively removes undefined values from an object
 * Firestore doesn't allow undefined values
 * IMPORTANT: Preserves Firestore FieldValue sentinels (e.g. serverTimestamp()) and Timestamp instances
 */
function sanitizeForFirestore(obj: any): any {
  if (obj === null || obj === undefined) {
    return null;
  }

  if (Array.isArray(obj)) {
    return obj.map(item => sanitizeForFirestore(item)).filter(item => item !== undefined);
  }

  if (typeof obj === 'object') {
    // Preserve Firestore Timestamp instances
    if (obj instanceof Timestamp) {
      return obj;
    }

    // Preserve Firestore FieldValue sentinels (e.g. serverTimestamp(), increment(), etc.)
    // These have an internal _methodName property that identifies them
    if (obj._methodName !== undefined || obj.type === 'AggregateField') {
      return obj;
    }

    // Preserve Date instances
    if (obj instanceof Date) {
      return obj;
    }

    const sanitized: any = {};
    Object.keys(obj).forEach(key => {
      const value = sanitizeForFirestore(obj[key]);
      if (value !== undefined) {
        sanitized[key] = value;
      }
    });
    return sanitized;
  }

  return obj;
}

export interface StoredAnalysisResult {
  id: string;
  userId: string;
  companyName: string;
  createdAt: FirestoreTimestamp;
  timestamp: FirestoreTimestamp; // For backward compatibility
  parameters: AnalysisParameters;
  results: Record<string, CheckResult>;
  documents: {
    audit_report?: { name: string; size: number; type: string };
    annexure_a?: { name: string; size: number; type: string };
    annexure_b?: { name: string; size: number; type: string };
    balance_sheet?: { name: string; size: number; type: string };
    notes?: { name: string; size: number; type: string };
    pl_notes?: { name: string; size: number; type: string };
    annual_report?: { name: string; size: number; type: string };
    secretarial_compliance?: { name: string; size: number; type: string };
    cfs?: { name: string; size: number; type: string }; // Consolidated Financial Statements document
  };
  summary: {
    total: number;
    compliant: number;
    nonCompliant: number;
    compliancePercentage: number;
  };
  userAnswers?: UserQuestionAnswer[];
}

export interface Company {
  id: string;
  name: string;
  createdAt: FirestoreTimestamp;
  userId: string;
}

export interface Partner {
  id: string;
  name: string;
  type: 'individual' | 'company' | 'subsidiary' | 'associate' | 'joint_venture';
  registrationNumber: string;
  createdAt: FirestoreTimestamp;
  userId: string;
}

export interface FirmSettings {
  firmName: string;
  registrationNumber: string;
  address?: string;
  contactEmail?: string;
  phone?: string;
  preferences?: {
    emailNotifications: boolean;
    smsNotifications: boolean;
    autoBackup: boolean;
    dataRetentionPeriod: number;
    defaultReportFormat: string;
    theme: string;
  };
  updatedAt: FirestoreTimestamp;
  userId: string;
}

export async function saveAnalysisResults(
  userId: string,
  parameters: AnalysisParameters,
  results: Record<string, CheckResult>,
  documentFiles: Record<string, File>,
  summary: {
    total: number;
    compliant: number;
    nonCompliant: number;
    compliancePercentage: number;
  },
  userAnswers?: UserQuestionAnswer[]
): Promise<string> {
  try {
    secureLog('🔍 Firestore: Saving analysis results for user:', { userId: '[REDACTED]' });
    secureLog('🔍 Firestore: User answers received:', {
      userAnswersLength: userAnswers?.length,
      userAnswersType: typeof userAnswers,
      userAnswers: userAnswers
    });

    // Validate inputs
    if (!userId || userId.trim() === '') {
      throw new Error('User ID is required for saving analysis results');
    }

    if (!parameters || !parameters.company_name) {
      throw new Error('Analysis parameters are required for saving');
    }

    if (!results || Object.keys(results).length === 0) {
      throw new Error('Analysis results are required for saving');
    }

    if (!summary || typeof summary.total !== 'number') {
      throw new Error('Valid summary is required for saving');
    }

    console.log('🔍 Firestore: Input validation passed');
    console.log('🔍 Firestore: Results count:', Object.keys(results).length);
    console.log('🔍 Firestore: Summary:', summary);
    console.log('🔍 Firestore: User answers count:', userAnswers?.length || 0);

    // Prepare document metadata
    const documents: Record<string, { name: string; size: number; type: string }> = {};

    Object.entries(documentFiles).forEach(([key, file]) => {
      if (file) {
        documents[key] = {
          name: file.name,
          size: file.size,
          type: file.type
        };
      }
    });

    // Calculate summary
    const calculatedSummary = {
      total: summary.total,
      compliant: summary.compliant,
      nonCompliant: summary.nonCompliant,
      compliancePercentage: summary.compliancePercentage
    };

    // Create the analysis data WITHOUT timestamp fields
    // serverTimestamp() sentinels must NEVER pass through sanitization or JSON.stringify
    const analysisData = {
      userId,
      companyName: parameters.company_name,
      parameters: {
        ...parameters,
        audit_date: parameters.audit_date.toISOString() // Convert Date to string for storage
      } as any,
      results,
      documents,
      summary: calculatedSummary,
      userAnswers: userAnswers || []
    };

    // Sanitize the analysis data to remove undefined values (no timestamps to corrupt)
    const sanitizedAnalysisData = sanitizeForFirestore(analysisData);

    console.log('🔍 Firestore: Sanitized data size:', JSON.stringify(sanitizedAnalysisData).length, 'characters');
    console.log('🔍 Firestore: Results count:', Object.keys(sanitizedAnalysisData.results || {}).length);
    console.log('🔍 Firestore: User answers count:', sanitizedAnalysisData.userAnswers?.length || 0);

    secureLog('🔍 Firestore: Analysis data being saved:', {
      userAnswersInData: analysisData.userAnswers,
      userAnswersLength: analysisData.userAnswers?.length
    });

    // Save to Firestore
    console.log('🔍 Firestore: Preparing to save to database...');
    console.log('🔍 Firestore: Firestore reference:', firestore);

    if (!firestore) {
      throw new Error('Firestore is not initialized');
    }

    const analysesCollection = collection(firestore, 'analyses');

    // Add serverTimestamp() ONLY at the very last moment, directly in addDoc
    // This guarantees no sanitization, JSON.stringify, or other processing can corrupt the sentinel
    const docRef = await addDoc(analysesCollection, {
      ...sanitizedAnalysisData,
      createdAt: serverTimestamp(),
      timestamp: serverTimestamp()
    });

    const analysisId = docRef.id;
    console.log('✅ Firestore: Analysis saved successfully with ID:', analysisId);
    secureLog('✅ Firestore: Analysis saved successfully with ID:', { analysisId });

    return analysisId;
  } catch (error) {
    secureLog('❌ Firestore: Error saving analysis results:', error);
    throw new Error(`Failed to save analysis results: ${error instanceof Error ? error.message : String(error)}`);
  }
}

export async function getAnalysisHistory(userId: string): Promise<StoredAnalysisResult[]> {
  try {
    secureLog('🔍 Firestore: Fetching analysis history for user:', { userId: '[REDACTED]' });
    console.log('🔍 Firestore: User ID:', userId);
    console.log('🔍 Firestore: Firestore instance:', firestore);

    if (!firestore) {
      throw new Error('Firestore is not initialized');
    }

    const analysesCollection = collection(firestore, 'analyses');
    console.log('🔍 Firestore: Collection reference:', analysesCollection);

    const userAnalysesQuery = query(
      analysesCollection,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );
    console.log('🔍 Firestore: Query created:', userAnalysesQuery);

    const snapshot = await getDocs(userAnalysesQuery);
    console.log('🔍 Firestore: Query executed successfully');
    console.log('🔍 Firestore: Snapshot size:', snapshot.size);
    console.log('🔍 Firestore: Querying for userId:', userId);

    secureLog('🔍 Firestore: Snapshot size:', { size: snapshot.size });

    if (snapshot.empty) {
      console.log('⚠️ Firestore: No data found for user:', userId);
      secureLog('🔍 Firestore: No data found for user:', { userId: '[REDACTED]' });
      return [];
    }

    console.log('✅ Firestore: Found', snapshot.size, 'analyses for user');

    const analyses: StoredAnalysisResult[] = [];

    snapshot.forEach((doc) => {
      const data = doc.data();
      secureLog('🔍 Firestore: Processing analysis from storage:', {
        analysisId: doc.id,
        hasUserAnswers: !!data.userAnswers,
        userAnswersLength: data.userAnswers?.length
      });

      // Ensure createdAt is properly converted to Timestamp if it's not already
      let createdAt = data.createdAt;
      if (createdAt && typeof createdAt === 'object' && !(createdAt instanceof Timestamp)) {
        // If it's a Firestore timestamp object with seconds, convert it to Timestamp
        if (createdAt.seconds !== undefined) {
          createdAt = new Timestamp(createdAt.seconds, createdAt.nanoseconds || 0);
        } else if (createdAt._seconds !== undefined) {
          createdAt = new Timestamp(createdAt._seconds, createdAt._nanoseconds || 0);
        }
      }

      analyses.push({
        id: doc.id,
        userId: data.userId,
        companyName: data.companyName,
        createdAt: createdAt,
        timestamp: createdAt, // Map createdAt to timestamp for backward compatibility
        parameters: {
          ...data.parameters,
          audit_date: new Date(data.parameters.audit_date) // Convert string back to Date
        },
        results: data.results,
        documents: data.documents,
        summary: data.summary,
        userAnswers: data.userAnswers || []
      });
    });

    // Sort by createdAt (descending - newest first)
    // Broken timestamps (e.g. {_methodName: 'serverTimestamp'}) get 0, pushing them to bottom
    analyses.sort((a, b) => {
      const getTime = (ts: any): number => {
        if (!ts) return 0;
        if (ts instanceof Timestamp) return ts.toMillis();
        if (ts instanceof Date) return ts.getTime();
        if (typeof ts === 'number') return ts;
        if (typeof ts === 'object' && typeof ts.seconds === 'number') return ts.seconds * 1000;
        if (typeof ts === 'object' && typeof ts._seconds === 'number') return ts._seconds * 1000;
        if (typeof ts === 'string') { const d = new Date(ts); return isNaN(d.getTime()) ? 0 : d.getTime(); }
        return 0;
      };
      return getTime(b.createdAt) - getTime(a.createdAt);
    });

    console.log('✅ Firestore: Retrieved', analyses.length, 'analyses from history');
    console.log('✅ Firestore: Analysis IDs:', analyses.map(a => a.id));
    console.log('✅ Firestore: Company names:', analyses.map(a => a.companyName));
    secureLog('✅ Firestore: Retrieved analysis history:', { count: analyses.length });
    return analyses;
  } catch (error) {
    secureLog('❌ Firestore: Error getting analysis history:', error);
    throw new Error(`Failed to get analysis history: ${error instanceof Error ? error.message : String(error)}`);
  }
}

export interface FSChecklistResult {
  id: string;
  userId: string;
  companyName: string;
  financialYear: string;
  signingPartner: string;
  personFilling: string;
  personFillingEmail: string;
  responses: Array<{
    checkId: string;
    checkName: string;
    checkDescription: string;
    checkCategory: string;
    response: string;
    commentary: string | null;
  }>;
  createdAt: FirestoreTimestamp;
  status: string;
}

export async function getFSChecklistHistory(userId: string): Promise<FSChecklistResult[]> {
  try {
    secureLog('🔍 Firestore: Fetching FS Checklist history for user:', { userId: '[REDACTED]' });

    if (!firestore) {
      throw new Error('Firestore is not initialized');
    }

    const checklistsCollection = collection(firestore, 'fs_checklists');
    const userChecklistsQuery = query(
      checklistsCollection,
      where('userId', '==', userId),
      orderBy('createdAt', 'desc')
    );

    const snapshot = await getDocs(userChecklistsQuery);

    if (snapshot.empty) {
      return [];
    }

    const checklists: FSChecklistResult[] = [];
    snapshot.forEach((doc) => {
      const data = doc.data();
      checklists.push({
        id: doc.id,
        userId: data.userId,
        companyName: data.companyName,
        financialYear: data.financialYear,
        signingPartner: data.signingPartner,
        personFilling: data.personFilling,
        personFillingEmail: data.personFillingEmail,
        responses: data.responses || [],
        createdAt: data.createdAt,
        status: data.status || 'submitted'
      });
    });

    return checklists;
  } catch (error) {
    secureLog('❌ Firestore: Error getting FS Checklist history:', error);
    throw new Error(`Failed to get FS Checklist history: ${error instanceof Error ? error.message : String(error)}`);
  }
}

export async function getFSChecklistById(checklistId: string, userId: string): Promise<FSChecklistResult | null> {
  try {
    secureLog('🔍 Firestore: Getting FS Checklist by ID:', { checklistId, userId: '[REDACTED]' });

    if (!firestore) {
      throw new Error('Firestore is not initialized');
    }

    const checklistDoc = doc(firestore, 'fs_checklists', checklistId);
    const snapshot = await getDoc(checklistDoc);

    if (!snapshot.exists()) {
      return null;
    }

    const data = snapshot.data();

    // Verify ownership
    if (data.userId !== userId) {
      throw new Error('Unauthorized access to checklist');
    }

    return {
      id: snapshot.id,
      userId: data.userId,
      companyName: data.companyName,
      financialYear: data.financialYear,
      signingPartner: data.signingPartner,
      personFilling: data.personFilling,
      personFillingEmail: data.personFillingEmail,
      responses: data.responses || [],
      createdAt: data.createdAt,
      status: data.status || 'submitted'
    };
  } catch (error) {
    secureLog('❌ Firestore: Error getting FS Checklist by ID:', error);
    throw new Error(`Failed to get FS Checklist: ${error instanceof Error ? error.message : String(error)}`);
  }
}

export async function getAnalysisById(analysisId: string, userId: string): Promise<StoredAnalysisResult | null> {
  try {
    secureLog('🔍 Firestore: Getting analysis by ID:', { analysisId, userId: '[REDACTED]' });

    if (!firestore) {
      throw new Error('Firestore is not initialized');
    }

    const analysisDoc = doc(firestore, 'analyses', analysisId);
    const snapshot = await getDoc(analysisDoc);

    if (!snapshot.exists()) {
      secureLog('🔍 Firestore: Analysis not found:', { analysisId });
      return null;
    }

    const data = snapshot.data();

    // Verify the analysis belongs to the user
    if (data.userId !== userId) {
      secureLog('🔍 Firestore: Analysis does not belong to user:', { analysisId, userId: '[REDACTED]' });
      return null;
    }

    // Ensure createdAt is properly converted to Timestamp if it's not already
    let createdAt = data.createdAt;
    if (createdAt && typeof createdAt === 'object' && !(createdAt instanceof Timestamp)) {
      // If it's a Firestore timestamp object with seconds, convert it to Timestamp
      if (createdAt.seconds !== undefined) {
        createdAt = new Timestamp(createdAt.seconds, createdAt.nanoseconds || 0);
      } else if (createdAt._seconds !== undefined) {
        createdAt = new Timestamp(createdAt._seconds, createdAt._nanoseconds || 0);
      }
    }

    const result: StoredAnalysisResult = {
      id: snapshot.id,
      userId: data.userId,
      companyName: data.companyName,
      createdAt: createdAt,
      timestamp: createdAt, // Map createdAt to timestamp for backward compatibility
      parameters: {
        ...data.parameters,
        audit_date: new Date(data.parameters.audit_date)
      },
      results: data.results,
      documents: data.documents,
      summary: data.summary,
      userAnswers: data.userAnswers || []
    };

    secureLog('✅ Firestore: Retrieved analysis by ID:', { analysisId });
    return result;
  } catch (error) {
    secureLog('❌ Firestore: Error getting analysis by ID:', error);
    throw new Error(`Failed to get analysis by ID: ${error instanceof Error ? error.message : String(error)}`);
  }
}

export async function deleteAnalysis(analysisId: string, userId: string): Promise<void> {
  try {
    secureLog('🗑️ Firestore: Deleting analysis:', { analysisId, userId: '[REDACTED]' });

    if (!firestore) {
      throw new Error('Firestore is not initialized');
    }

    const analysisDoc = doc(firestore, 'analyses', analysisId);

    // Verify the analysis exists and belongs to the user
    const snapshot = await getDoc(analysisDoc);
    if (!snapshot.exists()) {
      throw new Error('Analysis not found');
    }

    const data = snapshot.data();
    if (data.userId !== userId) {
      throw new Error('Analysis does not belong to user');
    }

    await deleteDoc(analysisDoc);
    secureLog('✅ Firestore: Analysis deleted successfully:', { analysisId });
  } catch (error) {
    secureLog('❌ Firestore: Error deleting analysis:', error);
    throw new Error(`Failed to delete analysis: ${error instanceof Error ? error.message : String(error)}`);
  }
}

export async function getFirmSettings(userId: string): Promise<any> {
  try {
    secureLog('🔍 Firestore: Getting firm settings for user:', { userId: '[REDACTED]' });

    if (!firestore) {
      throw new Error('Firestore is not initialized');
    }

    const firmSettingsDoc = doc(firestore, 'firm_settings', userId);
    const snapshot = await getDoc(firmSettingsDoc);

    secureLog('🔍 Firestore: Snapshot exists:', { exists: snapshot.exists() });

    if (!snapshot.exists()) {
      secureLog('🔍 Firestore: No firm settings found for user:', { userId: '[REDACTED]' });
      return null;
    }

    const settings = snapshot.data();
    secureLog('✅ Firestore: Retrieved firm settings:', { hasSettings: !!settings });
    return settings;
  } catch (error) {
    secureLog('❌ Firestore: Error getting firm settings:', error);
    throw new Error(`Failed to get firm settings: ${error instanceof Error ? error.message : String(error)}`);
  }
}

export async function updateFirmSettings(userId: string, settings: any): Promise<void> {
  try {
    secureLog('🔄 Firestore: Updating firm settings for user:', { userId: '[REDACTED]' });

    if (!firestore) {
      throw new Error('Firestore is not initialized');
    }

    const firmSettingsDoc = doc(firestore, 'firm_settings', userId);
    await setDoc(firmSettingsDoc, settings);

    secureLog('✅ Firestore: Firm settings updated successfully');
  } catch (error) {
    secureLog('❌ Firestore: Error updating firm settings:', error);
    throw new Error(`Failed to update firm settings: ${error instanceof Error ? error.message : String(error)}`);
  }
}

export async function saveFirmSettings(
  userId: string,
  firmName: string,
  registrationNumber: string,
  address?: string,
  contactEmail?: string,
  phone?: string,
  preferences?: {
    emailNotifications: boolean;
    smsNotifications: boolean;
    autoBackup: boolean;
    dataRetentionPeriod: number;
    defaultReportFormat: string;
    theme: string;
  }
): Promise<void> {
  try {
    secureLog('💾 Firestore: Saving firm settings for user:', { userId: '[REDACTED]' });

    if (!firestore) {
      throw new Error('Firestore is not initialized');
    }

    const firmSettingsDoc = doc(firestore, 'firm_settings', userId);

    const firmData: any = {
      firmName: firmName.trim(),
      registrationNumber: registrationNumber.trim(),
      updatedAt: serverTimestamp(),
      userId
    };

    // Add optional fields if provided
    if (address) firmData.address = address.trim();
    if (contactEmail) firmData.contactEmail = contactEmail.trim();
    if (phone) firmData.phone = phone.trim();
    if (preferences) firmData.preferences = preferences;

    await setDoc(firmSettingsDoc, firmData);

    secureLog('✅ Firestore: Firm settings saved successfully');
  } catch (error) {
    secureLog('❌ Firestore: Error saving firm settings:', error);
    throw new Error(`Failed to save firm settings: ${error instanceof Error ? error.message : String(error)}`);
  }
}

export async function getCompanies(userId: string): Promise<Company[]> {
  try {
    secureLog('🔍 Firestore: Getting companies for user:', { userId: '[REDACTED]' });
    console.log('🔍 Firestore: Getting companies for user:', userId);

    if (!firestore) {
      throw new Error('Firestore is not initialized');
    }

    const companiesCollection = collection(firestore, 'companies');
    console.log('🔍 Firestore: Companies collection reference:', companiesCollection);

    const companiesQuery = query(companiesCollection, where('userId', '==', userId));
    console.log('🔍 Firestore: Companies query created:', companiesQuery);

    const snapshot = await getDocs(companiesQuery);
    console.log('🔍 Firestore: Companies query executed successfully, size:', snapshot.size);

    if (snapshot.empty) {
      secureLog('🔍 Firestore: No companies found for user:', { userId: '[REDACTED]' });
      console.log('🔍 Firestore: No companies found for user, returning empty array');
      return [];
    }

    const companies: Company[] = [];

    snapshot.forEach((doc) => {
      const data = doc.data();
      companies.push({
        id: doc.id,
        name: data.name,
        createdAt: data.createdAt,
        userId: data.userId
      });
    });

    // Sort by createdAt (descending - newest first)
    companies.sort((a, b) => {
      const aTime = a.createdAt instanceof Timestamp ? a.createdAt.toMillis() : a.createdAt;
      const bTime = b.createdAt instanceof Timestamp ? b.createdAt.toMillis() : b.createdAt;
      return bTime - aTime;
    });

    secureLog('✅ Firestore: Retrieved companies:', { count: companies.length });
    return companies;
  } catch (error) {
    secureLog('❌ Firestore: Error getting companies:', error);
    throw new Error(`Failed to get companies: ${error instanceof Error ? error.message : String(error)}`);
  }
}

export async function addCompany(userId: string, name: string): Promise<string> {
  try {
    secureLog('➕ Firestore: Adding company for user:', { userId: '[REDACTED]', name });

    if (!firestore) {
      throw new Error('Firestore is not initialized');
    }

    const companiesCollection = collection(firestore, 'companies');

    const companyData: Omit<Company, 'id'> = {
      name: name.trim(),
      createdAt: serverTimestamp() as FirestoreTimestamp,
      userId
    };

    const docRef = await addDoc(companiesCollection, companyData);

    const companyId = docRef.id;
    secureLog('✅ Firestore: Company added successfully:', { companyId });

    return companyId;
  } catch (error) {
    secureLog('❌ Firestore: Error adding company:', error);
    throw new Error(`Failed to add company: ${error instanceof Error ? error.message : String(error)}`);
  }
}

export async function updateCompany(userId: string, companyId: string, newName: string): Promise<void> {
  try {
    secureLog('🔄 Firestore: Updating company:', { companyId, userId: '[REDACTED]', newName });

    if (!firestore) {
      throw new Error('Firestore is not initialized');
    }

    const companyDoc = doc(firestore, 'companies', companyId);
    await updateDoc(companyDoc, { name: newName.trim() });

    secureLog('✅ Firestore: Company updated successfully:', { companyId });
  } catch (error) {
    secureLog('❌ Firestore: Error updating company:', error);
    throw new Error(`Failed to update company: ${error instanceof Error ? error.message : String(error)}`);
  }
}

export async function deleteCompany(userId: string, companyId: string): Promise<void> {
  try {
    secureLog('🗑️ Firestore: Deleting company:', { companyId, userId: '[REDACTED]' });

    if (!firestore) {
      throw new Error('Firestore is not initialized');
    }

    const companyDoc = doc(firestore, 'companies', companyId);
    await deleteDoc(companyDoc);

    secureLog('✅ Firestore: Company deleted successfully:', { companyId });
  } catch (error) {
    secureLog('❌ Firestore: Error deleting company:', error);
    throw new Error(`Failed to delete company: ${error instanceof Error ? error.message : String(error)}`);
  }
}

export async function getPartners(userId: string): Promise<Partner[]> {
  try {
    secureLog('🔍 Firestore: Getting partners for user:', { userId: '[REDACTED]' });
    console.log('🔍 Firestore: Getting partners for user:', userId);

    if (!firestore) {
      throw new Error('Firestore is not initialized');
    }

    const partnersCollection = collection(firestore, 'partners');
    console.log('🔍 Firestore: Partners collection reference:', partnersCollection);

    const partnersQuery = query(partnersCollection, where('userId', '==', userId));
    console.log('🔍 Firestore: Partners query created:', partnersQuery);

    const snapshot = await getDocs(partnersQuery);
    console.log('🔍 Firestore: Partners query executed successfully, size:', snapshot.size);

    if (snapshot.empty) {
      secureLog('🔍 Firestore: No partners found for user:', { userId: '[REDACTED]' });
      console.log('🔍 Firestore: No partners found for user, returning empty array');
      return [];
    }

    const partners: Partner[] = [];

    snapshot.forEach((doc) => {
      const data = doc.data();
      partners.push({
        id: doc.id,
        name: data.name,
        type: data.type,
        registrationNumber: data.registrationNumber,
        createdAt: data.createdAt,
        userId: data.userId
      });
    });

    // Sort by createdAt (descending - newest first)
    partners.sort((a, b) => {
      const aTime = a.createdAt instanceof Timestamp ? a.createdAt.toMillis() : a.createdAt;
      const bTime = b.createdAt instanceof Timestamp ? b.createdAt.toMillis() : b.createdAt;
      return bTime - aTime;
    });

    secureLog('✅ Firestore: Retrieved partners:', { count: partners.length });
    return partners;
  } catch (error) {
    secureLog('❌ Firestore: Error getting partners:', error);
    throw new Error(`Failed to get partners: ${error instanceof Error ? error.message : String(error)}`);
  }
}

export async function addPartner(userId: string, name: string, registrationNumber: string, type: 'individual' | 'company' | 'subsidiary' | 'associate' | 'joint_venture' = 'company'): Promise<string> {
  try {
    secureLog('➕ Firestore: Adding partner for user:', { userId: '[REDACTED]', name, type });

    if (!firestore) {
      throw new Error('Firestore is not initialized');
    }

    const partnersCollection = collection(firestore, 'partners');

    const partnerData: Omit<Partner, 'id'> = {
      name: name.trim(),
      type: type,
      registrationNumber: registrationNumber.trim(),
      createdAt: serverTimestamp() as FirestoreTimestamp,
      userId
    };

    const docRef = await addDoc(partnersCollection, partnerData);

    const partnerId = docRef.id;
    secureLog('✅ Firestore: Partner added successfully:', { partnerId });

    return partnerId;
  } catch (error) {
    secureLog('❌ Firestore: Error adding partner:', error);
    throw new Error(`Failed to add partner: ${error instanceof Error ? error.message : String(error)}`);
  }
}

export async function updatePartner(userId: string, partnerId: string, newName: string, newRegistrationNumber: string): Promise<void> {
  try {
    secureLog('🔄 Firestore: Updating partner:', { partnerId, userId: '[REDACTED]', newName });

    if (!firestore) {
      throw new Error('Firestore is not initialized');
    }

    const partnerDoc = doc(firestore, 'partners', partnerId);
    await updateDoc(partnerDoc, {
      name: newName.trim(),
      registrationNumber: newRegistrationNumber.trim()
    });

    secureLog('✅ Firestore: Partner updated successfully:', { partnerId });
  } catch (error) {
    secureLog('❌ Firestore: Error updating partner:', error);
    throw new Error(`Failed to update partner: ${error instanceof Error ? error.message : String(error)}`);
  }
}

export async function deletePartner(userId: string, partnerId: string): Promise<void> {
  try {
    secureLog('🗑️ Firestore: Deleting partner:', { partnerId, userId: '[REDACTED]' });

    if (!firestore) {
      throw new Error('Firestore is not initialized');
    }

    const partnerDoc = doc(firestore, 'partners', partnerId);
    await deleteDoc(partnerDoc);

    secureLog('✅ Firestore: Partner deleted successfully:', { partnerId });
  } catch (error) {
    secureLog('❌ Firestore: Error deleting partner:', error);
    throw new Error(`Failed to delete partner: ${error instanceof Error ? error.message : String(error)}`);
  }
}

export async function deleteFirmSettings(userId: string): Promise<void> {
  try {
    secureLog('🗑️ Firestore: Deleting firm settings for user:', { userId: '[REDACTED]' });

    if (!firestore) {
      throw new Error('Firestore is not initialized');
    }

    const firmSettingsDoc = doc(firestore, 'firm_settings', userId);

    // Verify the settings exist
    const snapshot = await getDoc(firmSettingsDoc);
    if (!snapshot.exists()) {
      throw new Error('Firm settings not found');
    }

    await deleteDoc(firmSettingsDoc);

    secureLog('✅ Firestore: Firm settings deleted successfully');
  } catch (error) {
    secureLog('❌ Firestore: Error deleting firm settings:', error);
    throw new Error(`Failed to delete firm settings: ${error instanceof Error ? error.message : String(error)}`);
  }
}

/**
 * Helper function to get company names for dropdowns
 */
export async function getCompanyNames(userId: string): Promise<string[]> {
  try {
    const companies = await getCompanies(userId);
    return companies.map(company => company.name);
  } catch (error) {
    secureLog('❌ Firestore: Error fetching company names:', error);
    return [];
  }
}